import React, { useState } from 'react'
import Box from '@mui/material/Box'
import Paper from '@mui/material/Paper'
import Typography from '@mui/material/Typography'
import IconButton from '@mui/material/IconButton'
import Button from '@mui/material/Button'
import TextField from '@mui/material/TextField'
import MenuItem from '@mui/material/MenuItem'
import Link from '@mui/material/Link'
import DeleteIcon from '@mui/icons-material/Delete'
import AddIcon from '@mui/icons-material/Add'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import { router } from '@inertiajs/react'

export interface MergeRequestData {
  id: number
  url: string
  service_type: 'gitlab' | 'github'
  created_at: string
}

interface MergeRequestListProps {
  mergeRequests: MergeRequestData[]
  boardSlug: string
  taskId: number
}

function serviceLabel(serviceType: string): string {
  switch (serviceType) {
    case 'gitlab': return 'GitLab'
    case 'github': return 'GitHub'
    default: return serviceType
  }
}

function extractMrTitle(url: string): string {
  try {
    const u = new URL(url)
    const parts = u.pathname.split('/')
    // GitLab: /org/repo/-/merge_requests/123
    const mrIdx = parts.indexOf('merge_requests')
    if (mrIdx >= 0 && parts[mrIdx + 1]) {
      const repo = parts.slice(1, mrIdx - 1).join('/')
      return `${repo} !${parts[mrIdx + 1]}`
    }
    // GitHub: /org/repo/pull/123
    const prIdx = parts.indexOf('pull')
    if (prIdx >= 0 && parts[prIdx + 1]) {
      const repo = parts.slice(1, prIdx).join('/')
      return `${repo} #${parts[prIdx + 1]}`
    }
    return u.pathname
  } catch {
    return url
  }
}

export function MergeRequestList({ mergeRequests, boardSlug, taskId }: MergeRequestListProps) {
  const [showForm, setShowForm] = useState(false)
  const [url, setUrl] = useState('')
  const [serviceType, setServiceType] = useState<string>('gitlab')
  const [saving, setSaving] = useState(false)

  function handleAdd() {
    setSaving(true)
    router.post(`/api/v1/tasks/boards/${boardSlug}/tasks/${taskId}/merge_requests`, {
      merge_request: { url, service_type: serviceType },
    }, {
      preserveState: true,
      preserveScroll: true,
      onSuccess: () => {
        setUrl('')
        setShowForm(false)
        router.reload()
      },
      onFinish: () => setSaving(false),
    })
  }

  function handleDelete(mrId: number) {
    router.delete(`/api/v1/tasks/boards/${boardSlug}/tasks/${taskId}/merge_requests/${mrId}`, {
      preserveState: true,
      preserveScroll: true,
      onSuccess: () => router.reload(),
    })
  }

  return (
    <Paper elevation={0} sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1.5, mt: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
        <Typography
          variant="body2"
          fontWeight={600}
          color="text.secondary"
          sx={{ textTransform: 'uppercase', letterSpacing: '0.05em', fontSize: '0.7rem' }}
        >
          Merge Requests
        </Typography>
        {!showForm && (
          <IconButton
            size="small"
            onClick={() => setShowForm(true)}
            sx={{ opacity: 0.4, '&:hover': { opacity: 1 }, transition: 'opacity 0.15s' }}
          >
            <AddIcon sx={{ fontSize: 16 }} />
          </IconButton>
        )}
      </Box>

      {showForm && (
        <Box sx={{ mb: 2, display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'flex-start' }}>
          <TextField
            size="small"
            label="URL"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            disabled={saving}
            sx={{ flex: '1 1 200px' }}
            placeholder="https://gitlab.com/org/repo/-/merge_requests/1"
          />
          <TextField
            select
            size="small"
            label="Service"
            value={serviceType}
            onChange={(e) => setServiceType(e.target.value)}
            disabled={saving}
            sx={{ width: 120 }}
          >
            <MenuItem value="gitlab">GitLab</MenuItem>
            <MenuItem value="github">GitHub</MenuItem>
          </TextField>
          <Button size="small" variant="contained" onClick={handleAdd} disabled={saving || !url.trim()}>
            Add
          </Button>
          <Button size="small" onClick={() => { setShowForm(false); setUrl('') }} disabled={saving}>
            Cancel
          </Button>
        </Box>
      )}

      {mergeRequests.length === 0 && !showForm ? (
        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
          No merge requests yet.
        </Typography>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          {mergeRequests.map((mr) => (
            <Box
              key={mr.id}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                py: 0.5,
                px: 1,
                borderRadius: 1,
                '&:hover': { backgroundColor: 'action.hover' },
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  backgroundColor: mr.service_type === 'gitlab' ? '#FC6D26' : '#24292F',
                  color: '#fff',
                  px: 0.75,
                  py: 0.25,
                  borderRadius: 0.5,
                  fontWeight: 600,
                  fontSize: '0.65rem',
                  flexShrink: 0,
                }}
              >
                {serviceLabel(mr.service_type)}
              </Typography>
              <Link
                href={mr.url}
                target="_blank"
                rel="noopener noreferrer"
                variant="body2"
                sx={{
                  flex: 1,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                }}
              >
                {extractMrTitle(mr.url)}
                <OpenInNewIcon sx={{ fontSize: 12, flexShrink: 0 }} />
              </Link>
              <IconButton
                size="small"
                onClick={() => handleDelete(mr.id)}
                sx={{ opacity: 0.3, '&:hover': { opacity: 1 }, transition: 'opacity 0.15s' }}
              >
                <DeleteIcon sx={{ fontSize: 14 }} />
              </IconButton>
            </Box>
          ))}
        </Box>
      )}
    </Paper>
  )
}
